import { useState, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateCroRequestSchema,
  CreateSponsorRequestSchema,
  type CreateTrialRequest,
  type CroDto,
  type SponsorDto,
} from '@ctms/contracts';
import { z } from 'zod';
import { AlertCircle, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { AddNameDialog } from '../dictionary-panels.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';

export interface CreateTrialDialogProps {
  isOpen: boolean;
  canWrite: boolean;
  /** `tenant_admin` — inline add of sponsor/CRO mirrors the backend @RequireRole(TENANT_ADMIN). */
  isTenantAdmin: boolean;
  sponsors: SponsorDto[];
  cros: CroDto[];
  onClose: () => void;
  onCreated: () => void;
}

const CreateTrialFormSchema = z.object({
  protocolNumber: z.string().trim().min(1, 'Numer protokołu jest wymagany').max(100),
  shortTitle: z.string().trim().min(1, 'Krótki tytuł jest wymagany').max(255),
  shortcut: z
    .string()
    .trim()
    .min(1, 'Skrót jest wymagany')
    // #7 (Piotr odp.5): ≤10 chars wherever the shortcut is CONFIGURED.
    .max(10, 'Skrót może mieć maksymalnie 10 znaków'),
  siteNumber: z.string().trim().min(1, 'Numer ośrodka w badaniu jest wymagany').max(50),
  sponsorId: z.string().min(1, 'Sponsor jest obowiązkowy'),
  croId: z.string(),
  comment: z.string().max(10_000),
  active: z.boolean(),
});

type CreateTrialFormInput = z.infer<typeof CreateTrialFormSchema>;

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

function toCreateTrialRequest(data: CreateTrialFormInput): CreateTrialRequest {
  const request: CreateTrialRequest = {
    sponsorId: Number(data.sponsorId),
    protocolNumber: data.protocolNumber,
    shortTitle: data.shortTitle,
    shortcut: data.shortcut,
    siteNumber: data.siteNumber,
    randomizationNumber: false,
    active: data.active,
    travelCosts: false,
    labModule: false,
    compliance: false,
    paymentFrequency: 90,
    withholdingRate: 0,
    feeVariants: false,
    procedureVariants: false,
    dayZeroBased: false,
    medicationWindowEnabled: false,
  };
  const comment = data.comment.trim();
  if (comment.length > 0) {
    request.comment = comment;
  }
  if (data.croId.length > 0) {
    request.croId = Number(data.croId);
  }
  return request;
}

export function CreateTrialDialog({
  isOpen,
  canWrite,
  isTenantAdmin,
  sponsors,
  cros,
  onClose,
  onCreated,
}: CreateTrialDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const [croOpen, setCroOpen] = useState(false);

  const createSponsor = useMutation({
    mutationFn: (name: string) => {
      const body = CreateSponsorRequestSchema.parse({ name });
      return trialsApi.createSponsor(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.sponsors.all });
    },
  });

  const createCro = useMutation({
    mutationFn: (name: string) => {
      const body = CreateCroRequestSchema.parse({ name });
      return trialsApi.createCro(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.cros.all });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateTrialFormInput>({
    resolver: zodResolver(CreateTrialFormSchema),
    defaultValues: {
      protocolNumber: '',
      shortTitle: '',
      shortcut: '',
      siteNumber: '',
      sponsorId: '',
      croId: '',
      comment: '',
      active: true,
    },
  });

  if (!isOpen) {
    return null;
  }

  const handleClose = (): void => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  const onSubmit = async (data: CreateTrialFormInput): Promise<void> => {
    if (!canWrite) {
      setErrorMessage('Nie masz uprawnienia do zapisu badań.');
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await trialsApi.createTrial(toCreateTrialRequest(data));
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = await getApiErrorMessage(err, 'Nie udało się zapisać badania.');
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-trial-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-xs"
    >
      <div className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-800 pb-4">
          <div>
            <h2 id="create-trial-title" className="text-base font-semibold text-slate-100">
              Nowe badanie
            </h2>
            <p className="text-xs text-slate-400">
              Numer protokołu, krótki tytuł, skrót i numer ośrodka są wymagane. Sponsor jest obowiązkowy.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4" noValidate>
          {errorMessage && (
            <div
              role="alert"
              className="flex items-center gap-2.5 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-rose-400" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div>
            <label htmlFor="trial-protocol" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
              Numer protokołu
            </label>
            <input id="trial-protocol" type="text" disabled={isSubmitting} className={fieldClass} {...register('protocolNumber')} />
            {errors.protocolNumber && (
              <p className="mt-1 text-xs text-rose-400">{errors.protocolNumber.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="trial-title" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
              Krótki tytuł
            </label>
            <input id="trial-title" type="text" disabled={isSubmitting} className={fieldClass} {...register('shortTitle')} />
            {errors.shortTitle && <p className="mt-1 text-xs text-rose-400">{errors.shortTitle.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trial-shortcut" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
                Skrót
              </label>
              <input id="trial-shortcut" type="text" disabled={isSubmitting} className={fieldClass} {...register('shortcut')} />
              {errors.shortcut && <p className="mt-1 text-xs text-rose-400">{errors.shortcut.message}</p>}
            </div>
            <div>
              <label htmlFor="trial-site-number" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
                Numer ośrodka w badaniu
              </label>
              <input id="trial-site-number" type="text" disabled={isSubmitting} className={fieldClass} {...register('siteNumber')} />
              {errors.siteNumber && <p className="mt-1 text-xs text-rose-400">{errors.siteNumber.message}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="trial-sponsor" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
                Sponsor
              </label>
              <select id="trial-sponsor" disabled={isSubmitting} className={fieldClass} {...register('sponsorId')}>
                <option value="">Wybierz sponsora</option>
                {sponsors.map((sponsor) => (
                  <option key={sponsor.id} value={String(sponsor.id)}>
                    {sponsor.name}
                  </option>
                ))}
              </select>
              {isTenantAdmin && (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setSponsorOpen(true)}
                  className="mt-1.5 text-[11px] font-medium text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
                >
                  + Dodaj sponsora
                </button>
              )}
              {errors.sponsorId && <p className="mt-1 text-xs text-rose-400">{errors.sponsorId.message}</p>}
            </div>
            <div>
              <label htmlFor="trial-cro" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
                CRO
              </label>
              <select id="trial-cro" disabled={isSubmitting} className={fieldClass} {...register('croId')}>
                <option value="">— brak —</option>
                {cros.map((cro) => (
                  <option key={cro.id} value={String(cro.id)}>
                    {cro.name}
                  </option>
                ))}
              </select>
              {isTenantAdmin && (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setCroOpen(true)}
                  className="mt-1.5 text-[11px] font-medium text-cyan-400 hover:text-cyan-300 disabled:opacity-50"
                >
                  + Dodaj CRO
                </button>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="trial-comment" className="block text-xs font-medium uppercase tracking-wider text-slate-300">
              Komentarz
            </label>
            <textarea id="trial-comment" rows={2} disabled={isSubmitting} className={fieldClass} {...register('comment')} />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" disabled={isSubmitting} {...register('active')} />
            Badanie aktywne
          </label>

          {!canWrite && (
            <p className="text-xs text-amber-300">Nie masz uprawnienia do zapisu badań.</p>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !canWrite}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? 'Zapisywanie…' : 'Zapisz badanie'}
            </button>
          </div>
        </form>

        <AddNameDialog
          title="Dodaj sponsora"
          nameLabel="Nazwa sponsora"
          saveLabel="Zapisz sponsora"
          emptyMessage="Nazwa sponsora jest wymagana"
          isOpen={sponsorOpen}
          onClose={() => setSponsorOpen(false)}
          onSave={async (name) => {
            await createSponsor.mutateAsync(name);
          }}
        />
        <AddNameDialog
          title="Dodaj organizację CRO"
          nameLabel="Nazwa CRO"
          saveLabel="Zapisz CRO"
          emptyMessage="Nazwa CRO jest wymagana"
          isOpen={croOpen}
          onClose={() => setCroOpen(false)}
          onSave={async (name) => {
            await createCro.mutateAsync(name);
          }}
        />
      </div>
    </div>
  );
}
